import React, { Fragment } from 'react';
import { Button, Dropdown, Form, Modal, Radio, Dimmer, Loader, Checkbox } from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import ItemReminder from 'stores/ItemReminder';
import * as styles from './styles';
import { observable, IObservableArray, toJS } from 'mobx';
import MessageBox from 'components/MessageBox';
import MsgStore from 'stores/MsgStore';
import UiShared from 'stores/UiShared';
import Auth from 'stores/Auth';
import _ from 'lodash';
import API from 'api';

@inject(rootStore => ({
	auth: rootStore.auth,
	uiShared: rootStore.uiShared
}))
@observer
export default class ItemModal extends React.Component {
	props: {
		isOpen: boolean,
		itemReminderId: number,
		onRequestClose: () => {},
		onRequestReload: () => {},
		auth?: Auth,
		uiShared: UiShared
	};

	static defaultProps = {
		isOpen: false
	};

	msgStore: MsgStore = new MsgStore();
	@observable loading: boolean = false;
	@observable showButtons: boolean = false;
	@observable itemReminder: ItemReminder = null;
	@observable therapeuticAreas: Array<object> = [];
	@observable therapeuticAreaValue: number = 0;
	@observable drugValue: string = '';
	@observable drugs: Array<object> = [];
	@observable reminders: Array<object> = [];

	loadItemInfos = async () => {
		const iis = await API.get(
			`/ItemInfors?filter=${JSON.stringify({
				where: { disabled: false },
				fields: ['id', 'therapeuticAreaID', 'itemName']
			})}`
		);
		const uniqAreaIds = _.uniqBy(iis, 'therapeuticAreaID').map(o => o.therapeuticAreaID);
		uniqAreaIds.forEach(therapeuticAreaID => {
			let areaItems = _.filter(iis, { therapeuticAreaID: therapeuticAreaID });
			let areaNames = _.uniqBy(areaItems, 'itemName').map(item => item.itemName);
			this.drugs.push({
				therapeuticAreaID,
				items: areaNames.map((o, idx) => ({ key: idx, value: o, text: o }))
			});
		});
	};

	loadTherapeuticAreas = async () => {
		const res = await API.get('/TherapeuticAreas');
		this.therapeuticAreas = res.map(el => {
			return { key: el.id, value: el.id, text: el.name };
		});
	};

	loadReminders = async () => {
		this.reminders = await API.get(
			`/ItemReminders?filter=${JSON.stringify({
				fields: ['id', 'therapeuticAreaID', 'productName']
			})}`
		);
	};

	onClose = () => {
		this.resetForm();
		this.props.onRequestClose();
	};

	resetForm = async () => {
		this.itemReminder = new ItemReminder();
		this.therapeuticAreaValue = 0;
		this.drugValue = '';
		this.msgStore.clear();
	};

	componentDidMount = async () => {
		this.resetForm();
		await this.fetchLists();
	};

	componentDidUpdate = async prevProps => {
		if (this.props.isOpen && !prevProps.isOpen) {
			this.loading = true;
			this.resetForm();
			if (this.props.itemReminderId) {
				this.itemReminder = await ItemReminder.loadById(this.props.itemReminderId);
				this.therapeuticAreaValue = this.itemReminder.therapeuticAreaID;
				this.drugValue = this.itemReminder.productName;
			} else {
				await this.loadReminders();
			}
			this.showButtons = true;
			this.loading = false;
		}
	};

	fetchLists = async () => {
		await Promise.all([this.loadTherapeuticAreas(), this.loadItemInfos()]);
		const therapeuticAreaIDs = this.drugs.map(o => o.therapeuticAreaID);
		this.therapeuticAreas = this.therapeuticAreas.filter(
			o => therapeuticAreaIDs.indexOf(o.value) !== -1
		);
	};

	save = async () => {
		const { uiShared, onRequestReload } = this.props;
		const { itemReminder, onClose, therapeuticAreaValue, drugValue, therapeuticAreas } = this;
		try {
			this.msgStore.clear();
			uiShared.showSpinner('Saving...');
			this.itemReminder.therapeuticAreaID = therapeuticAreaValue;
			this.itemReminder.therapeuticArea = _.find(
				therapeuticAreas,
				o => o.value === therapeuticAreaValue
			)?.text;
			this.itemReminder.productName = drugValue;
			await itemReminder.update();
			await onRequestReload();
			onClose();
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			uiShared.hideModal();
		}
	};

	onChangeReset = val => {
		this.drugValue = '';
	};

	render() {
		const { isOpen } = this.props;
		const {
			itemReminder,
			onClose,
			therapeuticAreas,
			therapeuticAreaValue,
			loading,
			drugs,
			drugValue,
			reminders
		} = this;
		let headerTitle = 'Reminder Details';

		let drugsInArea = [];
		if (therapeuticAreaValue) {
			drugsInArea = _.find(drugs, o => o.therapeuticAreaID === therapeuticAreaValue)?.items;
			if (!this.props.itemReminderId) {
				const usedInArea = _.filter(
					reminders,
					o => o.therapeuticAreaID === therapeuticAreaValue
				).map(o => o.productName);
				drugsInArea = _.filter(drugsInArea, o => usedInArea.indexOf(o.value) === -1);
			}
		}

		return (
			itemReminder && (
				<Fragment>
					<Modal open={isOpen} size="small" onClose={onClose} closeOnDimmerClick={false}>
						<Fragment>
							<Dimmer active={loading} inverted>
								<Loader content="Loading" />
							</Dimmer>
							<Modal.Header>{headerTitle}</Modal.Header>
							<Modal.Content>
								<Form formNoValidate={true} autoComplete="off">
									<MessageBox msgStore={this.msgStore} />
									<Form.Group className={styles.formGroup} style={{ paddingTop: 10 }}>
										<Form.Field width={8}>
											<label>Therapeutic Area</label>
											<Dropdown
												options={therapeuticAreas}
												value={therapeuticAreaValue || ''}
												placeholder={'Select Therapeutic Area'}
												onChange={(e, data) => {
													this.therapeuticAreaValue = data.value;
													this.drugValue = '';
												}}
												width={8}
												selection
											/>
										</Form.Field>
										<div className="eight wide field"> </div>
										<Form.Field width={8}>
											<label>Drug Name</label>
											<Dropdown
												selection
												search={true}
												value={drugValue || ''}
												options={drugsInArea}
												onChange={(e, data) => {
													this.drugValue = data.value;
												}}
												width={8}
											/>
										</Form.Field>
										<div className="eight wide field"> </div>
									</Form.Group>
									<h4 className={styles.labelWrap}>Reminder Schedule</h4>
									<Form.Group inline>
										<Form.Field width={3}>
											<Dropdown
												selection
												search={true}
												value={itemReminder.schedule || ''}
												options={[...Array(32).keys()].map(o => ({ key: o, value: o, text: o }))}
												onChange={(e, data) => {
													this.itemReminder.schedule = data.value;
												}}
												width={3}
											/>
										</Form.Field>
										<div className="two wide field"> </div>
										<Form.Field width={2}>
											<Radio
												label="Days"
												name="radioGroup"
												value={1}
												checked={itemReminder.scheduleType === 1}
												onChange={(e, data) => {
													this.itemReminder.scheduleType = data.value;
												}}
											/>
										</Form.Field>
										<Form.Field width={2}>
											<Radio
												label="weeks"
												name="radioGroup"
												value={2}
												checked={itemReminder.scheduleType === 2}
												onChange={(e, data) => {
													this.itemReminder.scheduleType = data.value;
												}}
											/>
										</Form.Field>
										<Form.Field width={2}>
											<Radio
												label="months"
												name="radioGroup"
												value={3}
												checked={itemReminder.scheduleType === 3}
												onChange={(e, data) => {
													this.itemReminder.scheduleType = data.value;
												}}
											/>
										</Form.Field>
										<div className="five wide field"> </div>
									</Form.Group>
									<Form.Group>
										<div className={styles.toggleMargin}>
											<Checkbox
												label="Disabled"
												checked={itemReminder.disabled || false}
												onChange={() => (this.itemReminder.disabled = !itemReminder.disabled)}
												toggle
											/>
										</div>
									</Form.Group>

									<div className={styles.buttonsRowSpaced}>
										<div>
											<Button primary onClick={() => this.save()}>
												Save
											</Button>
											<Button onClick={onClose}>Close</Button>
										</div>
									</div>
								</Form>
							</Modal.Content>
						</Fragment>
					</Modal>
				</Fragment>
			)
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/Reminder/ItemModal.js